import type React from "react";
import { createContext, useContext, useRef, useState } from "react";
import { RemiErrorDeprecated } from "@remi/error";

import { useEffect } from "../hooks/useEffect";
import { cn } from "../utils/css";
import { RemiButton } from "./RemiButton";

// Auto-follow new content when the user is within this many pixels of the
// bottom edge in expanded mode. Past it, we assume they're reading older
// history and leave the scroll position alone.
const STICKY_BOTTOM_THRESHOLD_PX = 100;
// CSS transition for max-height is 300ms; force anchor-to-bottom on every
// ResizeObserver tick for slightly longer so the post-transition layout
// settles at the bottom regardless of where the user had scrolled before.
const EXPAND_TRANSITION_MS = 350;

/**
 * Resolve any CSS length (number, "400px", "70vh", "100%", "20rem", …) to
 * pixels in the context of a host element. Numbers pass through; strings get
 * measured via a hidden probe so we don't reimplement CSS unit math.
 */
function measureCssLengthPx(value: number | string, host: HTMLElement): number {
  if (typeof value === "number") {
    return value;
  }
  const probe = host.ownerDocument.createElement("div");
  probe.style.cssText = `position:absolute;visibility:hidden;width:0;height:${value};`;
  host.appendChild(probe);
  const px = probe.offsetHeight;
  host.removeChild(probe);
  return px;
}

/** Walk up from `el` to the nearest scrollable ancestor; fall back to window. */
function findScrollableAncestor(el: HTMLElement): HTMLElement | Window {
  let cur: HTMLElement | null = el.parentElement;
  while (cur) {
    const overflowY = cur.ownerDocument.defaultView?.getComputedStyle(cur).overflowY;
    if ((overflowY === "auto" || overflowY === "scroll") && cur.scrollHeight > cur.clientHeight) {
      return cur;
    }
    cur = cur.parentElement;
  }
  return el.ownerDocument.defaultView ?? window;
}

type RemiClampedRevealAnchor = "top" | "bottom";

interface RemiClampedRevealContextValue {
  expanded: boolean;
  isOverflowing: boolean;
  toggle: () => void;
  setContentEl: (el: HTMLDivElement | null) => void;
  setScrollEl: (el: HTMLDivElement | null) => void;
  previewMaxHeight: number;
  expandedMaxHeight: number | string;
  anchor: RemiClampedRevealAnchor;
}

const RemiClampedRevealContext = createContext<RemiClampedRevealContextValue | null>(null);

function useClampedRevealContext(component: string): RemiClampedRevealContextValue {
  const ctx = useContext(RemiClampedRevealContext);
  if (!ctx) {
    throw new RemiErrorDeprecated({
      message: `${component} must be rendered inside <RemiClampedReveal>.`,
    });
  }
  return ctx;
}

interface RemiClampedRevealProps {
  /** Pixel height to clamp the preview to. Overflow detection is measured against this value. */
  previewMaxHeight: number;
  /** Max height when expanded. Accepts any CSS height string (e.g. "70vh") or a number of pixels. */
  expandedMaxHeight?: number | string;
  defaultExpanded?: boolean;
  /** Edge kept visible while clamped: "bottom" (default) auto-follows newest content, chat-style; "top" holds the start still and never touches scroll. */
  anchor?: RemiClampedRevealAnchor;
  children: React.ReactNode;
}

const RemiClampedReveal = ({
  previewMaxHeight,
  expandedMaxHeight = "100%",
  defaultExpanded = false,
  anchor = "bottom",
  children,
}: RemiClampedRevealProps) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  // Callback refs so observers re-attach when the underlying node is swapped
  // (e.g. when a conditional wrapper toggles and remounts the subtree).
  // A useRef + [] useEffect would silently keep observing a detached node.
  const [contentEl, setContentEl] = useState<HTMLDivElement | null>(null);
  const [scrollEl, setScrollEl] = useState<HTMLDivElement | null>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  // Tracked in refs so the ResizeObserver effect doesn't tear down on every
  // expand toggle (which would lose observations mid-transition).
  const expandedRef = useRef(expanded);
  expandedRef.current = expanded;
  const forceAnchorUntilRef = useRef(0);

  useEffect(() => {
    if (!contentEl || !scrollEl) {
      return;
    }
    const update = () => {
      setIsOverflowing(contentEl.scrollHeight > previewMaxHeight);
      if (anchor === "top") {
        return;
      }
      // Sticky-bottom: the newest content lives at the end of the scroll area.
      // Force anchor when:
      //   - collapsed (the user can't scroll, so always show newest)
      //   - inside the expand-toggle transition window (clientHeight is mid-
      //     animation; a single scrollTop=scrollHeight assignment gets clamped
      //     to the interpolated value, leaving the view off the bottom)
      // Otherwise follow only when the user is already near the bottom — past
      // that they're reading older history and we mustn't hijack their scroll.
      const forced = !expandedRef.current || Date.now() < forceAnchorUntilRef.current;
      if (forced) {
        scrollEl.scrollTop = scrollEl.scrollHeight;
        return;
      }
      const distanceFromBottom = scrollEl.scrollHeight - scrollEl.scrollTop - scrollEl.clientHeight;
      if (distanceFromBottom < STICKY_BOTTOM_THRESHOLD_PX) {
        scrollEl.scrollTop = scrollEl.scrollHeight;
      }
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(contentEl);
    observer.observe(scrollEl);
    return () => observer.disconnect();
  }, [contentEl, scrollEl, previewMaxHeight, anchor]);

  const toggle = () => {
    if (anchor === "top") {
      // Collapsing must re-show the anchored top edge, not wherever the user scrolled while expanded.
      if (scrollEl && expandedRef.current) {
        scrollEl.scrollTop = 0;
      }
      setExpanded((prev) => !prev);
      return;
    }
    // Pre-scroll the page by the height delta so the trigger appears to stay
    // anchored in viewport while the max-height transition plays. Both the
    // page scroll and the max-height interp run for ~300ms with smooth
    // easing, masking the layout shift.
    if (scrollEl && contentEl) {
      const isExpanding = !expandedRef.current;
      const host = scrollEl.parentElement;
      if (host) {
        const currentMax = isExpanding ? previewMaxHeight : expandedMaxHeight;
        const nextMax = isExpanding ? expandedMaxHeight : previewMaxHeight;
        const currentHeight = Math.min(measureCssLengthPx(currentMax, host), contentEl.scrollHeight);
        const nextHeight = Math.min(measureCssLengthPx(nextMax, host), contentEl.scrollHeight);
        const delta = nextHeight - currentHeight;
        if (delta !== 0) {
          findScrollableAncestor(scrollEl).scrollBy({ top: delta, behavior: "smooth" });
        }
      }
    }
    forceAnchorUntilRef.current = Date.now() + EXPAND_TRANSITION_MS;
    setExpanded((prev) => !prev);
  };

  return (
    <RemiClampedRevealContext.Provider
      value={{
        expanded,
        isOverflowing,
        toggle,
        setContentEl,
        setScrollEl,
        previewMaxHeight,
        expandedMaxHeight,
        anchor,
      }}
    >
      {children}
    </RemiClampedRevealContext.Provider>
  );
};

interface RemiClampedRevealContentProps {
  className?: string;
  /** When true (default), shows a fade-out gradient over the top edge while collapsed. */
  fadeMask?: boolean;
  children: React.ReactNode;
}

const RemiClampedRevealContent = ({ className, fadeMask = true, children }: RemiClampedRevealContentProps) => {
  const { expanded, isOverflowing, setContentEl, setScrollEl, previewMaxHeight, expandedMaxHeight, anchor } =
    useClampedRevealContext("RemiClampedRevealContent");

  return (
    <div className="relative">
      <div
        ref={setScrollEl}
        className={cn(
          "transition-[max-height] duration-300",
          expanded ? "overflow-y-auto" : "overflow-hidden",
          className,
        )}
        style={{ maxHeight: expanded ? expandedMaxHeight : previewMaxHeight }}
      >
        <div ref={setContentEl} className="flex w-full flex-col">
          {children}
        </div>
      </div>

      {!expanded && isOverflowing && fadeMask && (
        <div
          className={cn(
            "pointer-events-none absolute inset-x-0 h-16",
            anchor === "top"
              ? "bottom-0 bg-linear-to-t from-background-primary to-transparent"
              : "top-0 bg-linear-to-b from-background-primary to-transparent",
          )}
        />
      )}
    </div>
  );
};

interface RemiClampedRevealTriggerProps {
  expandLabel?: string;
  collapseLabel?: string;
  className?: string;
}

const RemiClampedRevealTrigger = ({
  expandLabel = "View more",
  collapseLabel = "Show less",
  className,
}: RemiClampedRevealTriggerProps) => {
  const { expanded, isOverflowing, toggle } = useClampedRevealContext("RemiClampedRevealTrigger");
  if (!isOverflowing) {
    return null;
  }
  return (
    <RemiButton muted size="sm" aria-expanded={expanded} onClick={toggle} className={className}>
      {expanded ? collapseLabel : expandLabel}
    </RemiButton>
  );
};

export { RemiClampedReveal, RemiClampedRevealContent, RemiClampedRevealTrigger };
